/**
 * <TutorAlertRow> — une alerte de « À traiter » : une rangée compacte et teintée (molecule).
 *
 * Trois poids visuels dans le live du masteur : une **alerte** est une rangée teintée, une **proposition** est une
 * carte (`<TutorSessionFeedCard>`, `<TutorSessionEntry>`), une **trace** est un point sur le fil de l'élève. Une
 * alerte dit qu'un élève a besoin du masteur maintenant (la main, la sortie de l'appli depuis 3 min, le silence) :
 * le prénom en gras, la phrase entière (deux lignes au plus), son action au bout — ou dessous, quand la rangée est trop
 * étroite pour les deux.
 *
 * La teinte dit le signal : la main sur l'alerte orangée, la sortie sur l'encart et un liseré orangé, le silence sur
 * l'encart et un liseré violet (la couleur du Concentré, celle du silence sur le fil) ; l'escalade (sans réponse) passe
 * au rouge et le dit. Toucher la phrase regarde l'élève (« Voir <prénom> »). La croix écarte l'alerte ; sur une main
 * levée, la coche la baisse.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UITutorFeedItem } from "../../../ports/primary/ui/types/tutorLive"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { inkOnFill, type SemanticColorKey } from "../../theme/tokens/colors"
import { SignedAvatar } from "../SignedAvatar"
import { tutorFeedSign } from "../TutorSessionFeedCard"

export type TutorAlertRowProps = {
    item: UITutorFeedItem
    onAction: (actionId: string) => void
    /** Regarder l'élève : la phrase devient « Voir <prénom> ». Absent : la phrase ne se touche pas. */
    onLook?: () => void
    /** La croix (la coche sur une main levée), sur une alerte `dismissible`. */
    onDismiss?: () => void
    pendingActionId?: string
    testID?: string
}

const TEXTS = {
    escalated: "Sans réponse — à toi de jouer",
    look: (firstname: string) => `Voir ${firstname}`,
    dismiss: "Écarter",
    lowerHand: (firstname: string) => `Baisser la main de ${firstname}`,
}

type AlertFace = { background: SemanticColorKey; rail: SemanticColorKey; sign: SemanticColorKey }

/** La teinte de chaque alerte ; l'escalade passe avant le signal. */
function alertFace(item: UITutorFeedItem): AlertFace {
    if (item.escalated === true) return { background: "dangerLight", rail: "danger", sign: "danger" }
    if (item.signal === "hand_raised") return { background: "warningLight", rail: "warning", sign: "warning" }
    if (item.signal === "silence") return { background: "surface-subtle", rail: "purple", sign: "purple" }
    return { background: "surface-subtle", rail: "warning", sign: "warning" }
}

const AVATAR_SIZE = 24

/**
 * La largeur où l'action tient au bout de la phrase sans la couper (relevé en recette du 05/11 : « Lina n'a pas p… » à
 * côté de « Parler à Lina », dans la colonne à 1440 comme à 1024 px). Plus étroite, l'action passe sous la phrase.
 */
const ACTION_BESIDE_FROM = 440

export function TutorAlertRow(props: TutorAlertRowProps) {
    const { item, onAction, onLook, onDismiss, pendingActionId, testID } = props
    const face = alertFace(item)
    const { firstname } = item.student
    const lowersHand = item.signal === "hand_raised"
    const action = item.action
    const escalated = item.escalated === true
    const { onLayout, atLeast: actionBeside } = useWidthAtLeast(ACTION_BESIDE_FROM)
    const sentence = (
        <Text size="text-sm" color="text-primary" numberOfLines={2}>
            <Text size="text-sm" weight="bold" color="text-strong">
                {firstname}
            </Text>{" "}
            {item.text}
        </Text>
    )
    const actionButton =
        action == null ? null : action.passive === true ? (
            <TextButton
                label={action.label}
                size="sm"
                loading={action.id === pendingActionId}
                onPress={() => onAction(action.id)}
            />
        ) : (
            <Button
                label={action.label}
                size="sm"
                color={escalated ? "danger" : undefined}
                loading={action.id === pendingActionId}
                onPress={() => onAction(action.id)}
            />
        )
    return (
        <View style={styles.root(face.background, face.rail)} onLayout={onLayout} testID={testID}>
            <Stack gap="1">
                <Row gap="2" align="center">
                    <SignedAvatar
                        uri={item.student.avatarUrl}
                        name={firstname}
                        size={AVATAR_SIZE}
                        sign={{ icon: tutorFeedSign(item).icon, color: face.sign, ink: inkOnFill(face.sign) }}
                    />
                    {onLook != null ? (
                        <Pressable
                            onPress={onLook}
                            accessibilityRole="button"
                            accessibilityLabel={`${TEXTS.look(firstname)} : ${item.text}`}
                            style={styles.flex}
                        >
                            {sentence}
                        </Pressable>
                    ) : (
                        <View style={styles.flex}>{sentence}</View>
                    )}
                    <Text size="text-xs" color="text-muted">
                        {item.elapsed}
                    </Text>
                    {actionBeside && actionButton}
                    {item.dismissible === true && onDismiss != null && (
                        <IconButton
                            iconName={lowersHand ? "check" : "times"}
                            appearance="transparent"
                            iconColor="text-muted"
                            size={24}
                            iconSize={14}
                            onPress={onDismiss}
                            accessibilityLabel={
                                lowersHand ? TEXTS.lowerHand(firstname) : `${TEXTS.dismiss} : ${firstname} ${item.text}`
                            }
                        />
                    )}
                </Row>
                {escalated && (
                    <Text size="text-xs" weight="bold" color="danger-strong" style={styles.under}>
                        {TEXTS.escalated}
                    </Text>
                )}
                {!actionBeside && actionButton != null && <View style={styles.underAction}>{actionButton}</View>}
            </Stack>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: (background: SemanticColorKey, rail: SemanticColorKey) => ({
        backgroundColor: theme.colors[background],
        borderLeftWidth: 3,
        borderLeftColor: theme.colors[rail],
        borderRadius: theme.radius.md,
        paddingVertical: theme.spacing["1"],
        paddingHorizontal: theme.spacing["2"],
    }),
    flex: { flex: 1, minWidth: 0 },
    under: { paddingLeft: AVATAR_SIZE + theme.spacing["2"] },
    underAction: { paddingLeft: AVATAR_SIZE + theme.spacing["2"], alignItems: "flex-start" },
}))
